import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type {
  BillPeriod,
  BillPeriodListApiQueryOptions,
} from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import { toHttpError } from "@/utils/api"
import { fetchAllPages } from "@/utils/fetch-all-pages"
import { useQuery } from "@tanstack/react-query"

import { getBillPeriods } from "./get-bill-periods"

/** Matches `BillPeriodsListQuery::MAX_LIMIT`. A larger limit is capped by the proxy. */
const ALL_BILL_PERIODS_PAGE_SIZE = 100

// A circuit breaker, not an expected ceiling. Hitting it is an error, not a
// shorter total the chart can draw.
const ALL_BILL_PERIODS_MAX_PAGES = 100

interface UseAllBillPeriodsOptions {
  apiQueryOptions: Omit<BillPeriodListApiQueryOptions, "offset">
  maxPages?: number
  organizationId: Organization["id"]
}

export const useAllBillPeriods = ({
  apiQueryOptions,
  maxPages = ALL_BILL_PERIODS_MAX_PAGES,
  organizationId,
}: UseAllBillPeriodsOptions) => {
  const queryKey = useQueryKey(ApiQueryName.BillPeriods, "getMany", null, {
    apiQueryOptions,
    maxPages,
    organizationId,
    pageThrough: true,
  })

  const queryResult = useQuery({
    enabled: Boolean(organizationId),
    queryFn: (): Promise<BillPeriod[]> =>
      fetchAllPages({
        fetchPage: (offset) =>
          getBillPeriods({
            apiQueryOptions: {
              ...apiQueryOptions,
              limit: apiQueryOptions.limit ?? ALL_BILL_PERIODS_PAGE_SIZE,
              offset,
            },
            organizationId,
          }),
        incompleteError: () =>
          toHttpError(new Error("Bill period results exceeded the page cap")),
        maxPages,
      }),
    queryKey,
    retry: false,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.BillPeriods)
}
